import { Delete, Edit } from '@mui/icons-material'
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from '@mui/material'
import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IInfoCenterReport } from 'types/info-center/reports'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { useBoolean } from 'hooks/useBoolean'
import { DialogCreateReport } from './dialog-create-report'

interface Props {
  reports: IInfoCenterReport[]
  onDelete?: (planId: string) => void
  canEdit?: boolean
}

const StyledTable = styled(Table)(() => ({
  borderCollapse: 'collapse',
  width: '100%',
  '& th, & td': {
    border: '1px solid #dddddd !important',
  },
  '& th': {
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-root': {
    paddingLeft: '12px',
    paddingRight: '12px',
  },
}))

export const ReportTable: FunctionComponent<Props> = ({
  reports,
  onDelete,
  canEdit = true,
}) => {
  const { t } = useTranslation()
  const dialogDeleteReport = useBoolean(false)
  const [reportId, setReportId] = useState('')
  const [selectedReport, setSelectedReport] = useState<IInfoCenterReport>()
  const dialogCreateReport = useBoolean(false)

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <ConfirmationDialog
        open={dialogDeleteReport.isTrue}
        onClose={dialogDeleteReport.setFalse}
        onConfirm={() => onDelete!(reportId as string)}
        text={t('are-you-sure-you-want-to-delete')}
      />
      {dialogCreateReport.isTrue && (
        <DialogCreateReport
          onClose={dialogCreateReport.setFalse}
          report={selectedReport}
        />
      )}
      <TableContainer>
        <StyledTable size="small" sx={{ minWidth: 650, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('title')}</TableCell>
              <TableCell>{t('date')}</TableCell>
              {canEdit && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <TableRow
                key={report.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell scope="row">{report.title}</TableCell>
                <TableCell scope="row">
                  {formatTimeStampToDate(report.publishedAt)}
                </TableCell>
                {canEdit && (
                  <TableCell sx={{ width: '100px' }}>
                    <IconButton
                      onClick={() => {
                        setSelectedReport(report)
                        dialogCreateReport.setTrue()
                      }}
                    >
                      <Edit color="primary" fontSize="small" />
                    </IconButton>
                    {onDelete && (
                      <IconButton
                        onClick={() => {
                          setReportId(report.id)
                          dialogDeleteReport.setTrue()
                        }}
                      >
                        <Delete color="error" fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Paper>
  )
}
